import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import { Navigation, Pagination, EffectCoverflow, Autoplay } from 'swiper/modules';
import { FaExpand, FaTimes } from 'react-icons/fa';

import bg1 from '../assets/bg1.jpg';
import bg2 from '../assets/bg2.jpg';
import bg3 from '../assets/bg3.jpg';
import bg4 from '../assets/bg4.jpg';
import bg5 from '../assets/bg5.png';
import bg6 from '../assets/bg6.png';
import bg7 from '../assets/bg7.png';

const Trainers = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const sectionRef = useRef(null);

  const images = [bg1, bg2, bg3, bg4, bg5, bg6, bg7];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  return (
    <Container id="galeria" ref={sectionRef}>
      <BackgroundGlow />
      
      <Content>
        <HeaderSection $isVisible={isVisible}>
          <SectionBadge>
            <span>📸 NOSSA ESTRUTURA</span>
          </SectionBadge>
          <Title>
            CONHEÇA A <HighlightText>M30 FITNESS</HighlightText>
          </Title>
          <Subtitle>
            Um espaço pensado para sua evolução. Equipamentos modernos e ambiente motivador.
          </Subtitle>
        </HeaderSection>

        <GalleryWrapper $isVisible={isVisible}>
          <Swiper
            modules={[Navigation, Pagination, EffectCoverflow, Autoplay]}
            effect="coverflow"
            grabCursor={true}
            centeredSlides={true}
            slidesPerView="auto"
            loop={true}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            coverflowEffect={{
              rotate: 0,
              stretch: 0,
              depth: 100,
              modifier: 2.5,
              slideShadows: false,
            }}
            navigation
            pagination={{ clickable: true }}
            breakpoints={{
              320: { slidesPerView: 1 },
              640: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
          >
            {images.map((image, index) => (
              <SwiperSlide key={index}>
                <ImageCard onClick={() => openLightbox(image)}>
                  <ImageWrapper>
                    <CardImage src={image} alt={`M30 Fitness ${index + 1}`} />
                    <ImageOverlay>
                      <ExpandIcon>
                        <FaExpand />
                      </ExpandIcon>
                      <OverlayText>Ver Imagem</OverlayText>
                    </ImageOverlay>
                  </ImageWrapper>
                  <CardGlow />
                </ImageCard>
              </SwiperSlide>
            ))}
          </Swiper>
        </GalleryWrapper>

        <GridGallery $isVisible={isVisible}>
          {images.map((image, index) => (
            <GridItem 
              key={index} 
              $delay={`${index * 0.1}s`}
              $isVisible={isVisible}
              onClick={() => openLightbox(image)}
            >
              <GridImage src={image} alt={`M30 Fitness ${index + 1}`} />
              <GridOverlay>
                <FaExpand />
              </GridOverlay>
              <GridGlow />
            </GridItem>
          ))}
        </GridGallery>
      </Content>

      {selectedImage && (
        <Lightbox onClick={closeLightbox}>
          <LightboxContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeLightbox}>
              <FaTimes />
            </CloseButton>
            <LightboxImage src={selectedImage} alt="M30 Fitness" />
          </LightboxContent>
        </Lightbox>
      )}
    </Container>
  );
};

export default Trainers;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.5;
  }
`;

const Container = styled.section`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, var(--bg-dark) 0%, #0d0d0d 50%, var(--bg-dark) 100%);
  position: relative;
  padding: 60px 0;
  overflow: hidden;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, var(--primary-glow) 0%, transparent 70%);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.2;
  pointer-events: none;
  animation: ${pulse} 6s ease-in-out infinite;
`;

const Content = styled.div`
  width: 90%;
  max-width: 1200px;
  position: relative;
  z-index: 1;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 60px;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInUp} 0.8s ease forwards;
  `}
`;

const SectionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background: rgba(229, 168, 0, 0.1);
  border: 1px solid rgba(229, 168, 0, 0.2);
  border-radius: 50px;
  padding: 10px 25px;
  margin-bottom: 25px;

  span {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 600;
    font-size: 0.85rem;
    color: var(--primary-color);
    letter-spacing: 3px;
  }
`;

const Title = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 15px;
`;

const HighlightText = styled.span`
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.1rem;
  color: #888;
  max-width: 500px;
  margin: 0 auto;
`;

const GalleryWrapper = styled.div`
  display: none;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInUp} 0.8s ease 0.2s forwards;
  `}

  @media (max-width: 768px) {
    display: block;
  }

  .swiper {
    padding: 40px 0;
  }

  .swiper-slide {
    width: 280px;
    height: 350px;
  }

  .swiper-button-next,
  .swiper-button-prev {
    color: var(--primary-color) !important;
    
    &::after {
      font-size: 1.5rem;
    }
  }

  .swiper-pagination-bullet {
    background: #444 !important;
    opacity: 1;
  }

  .swiper-pagination-bullet-active {
    background: var(--primary-color) !important;
  }
`;

const GridGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 25px;
  opacity: 0;
  
  ${({ $isVisible }) => $isVisible && css`
    animation: ${fadeInUp} 0.8s ease 0.2s forwards;
  `}

  @media (max-width: 768px) {
    display: none;
  }

  & > *:nth-child(1),
  & > *:nth-child(2),
  & > *:nth-child(3),
  & > *:nth-child(4),
  & > *:nth-child(5),
  & > *:nth-child(6) {
    grid-column: span 2;
  }

  & > *:nth-child(7) {
    grid-column: 3 / 5;
  }
`;

const GridItem = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  opacity: 0;
  
  ${({ $isVisible, $delay }) => $isVisible && css`
    animation: ${scaleIn} 0.6s ease forwards;
    animation-delay: ${$delay};
  `}

  &:hover {
    transform: scale(1.03);
    z-index: 2;
  }

  transition: transform 0.4s ease;
`;

const GridImage = styled.img`
  width: 100%;
  height: 280px;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${GridItem}:hover & {
    transform: scale(1.1);
  }
`;

const GridOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(0, 0, 0, 0.3) 50%,
    rgba(0, 0, 0, 0.8) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  svg {
    color: var(--primary-color);
    font-size: 2rem;
    transform: translateY(20px);
    transition: transform 0.3s ease;
  }

  ${GridItem}:hover & {
    opacity: 1;
    
    svg {
      transform: translateY(0);
    }
  }
`;

const GridGlow = styled.div`
  position: absolute;
  inset: 0;
  border: 2px solid transparent;
  border-radius: 20px;
  transition: all 0.3s ease;
  pointer-events: none;
  
  ${GridItem}:hover & {
    border-color: var(--primary-color);
    box-shadow: 0 0 30px var(--primary-glow),
                inset 0 0 30px rgba(229, 168, 0, 0.1);
  }
`;

const ImageCard = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${ImageCard}:hover & {
    transform: scale(1.1);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${ImageCard}:hover & {
    opacity: 1;
  }
`;

const ExpandIcon = styled.div`
  width: 50px;
  height: 50px;
  background: var(--primary-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 1.2rem;
`;

const OverlayText = styled.span`
  font-family: 'Rajdhani', sans-serif;
  font-size: 0.9rem;
  color: #fff;
  font-weight: 600;
`;

const CardGlow = styled.div`
  position: absolute;
  inset: 0;
  border: 2px solid transparent;
  border-radius: 20px;
  transition: all 0.3s ease;
  pointer-events: none;
  
  ${ImageCard}:hover & {
    border-color: var(--primary-color);
    box-shadow: 0 0 20px var(--primary-glow);
  }
`;

const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 40px;
  animation: ${fadeIn} 0.3s ease;
`;

const LightboxContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 10px;
  animation: ${scaleIn} 0.3s ease;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -50px;
  right: 0;
  background: var(--primary-color);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 0 20px var(--primary-glow);
  }
`;
